import { useQuery } from "@apollo/client";
import CircularProgress from "@mui/material/CircularProgress";
import * as types from "_graphql-types/graphql";

import classNames from "classnames";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updateFilter } from "Reducers/globalSearchV2/globalSearchV2.actions";

import { INVESTMENT_PATHS } from "Components/GlobalSearchWorkspace/GlobalSearchControls.component";
import { FavoriteInvestment } from "./FavoriteInvestment";
import { FETCH_SET_INVESTMENTS, QUERY_CURATED_SETS } from "./graphql";
import { useDispatch } from "src/react-redux";

interface SetTitleProps {
  setName: string;
  numberInvestments: number | undefined;
  setId: number;
  setExpanded: (arg0: boolean) => void;
  expanded: boolean;
}

function SetTitle({
  setName,
  numberInvestments,
  setExpanded,
  expanded,
  setId,
}: SetTitleProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return (
    <div className="main-tree__item-header">
      <span
        className={classNames("main-tree__item-header-toggle", "icon", {
          "icon-arrow": expanded,
          "icon-arrow-right": !expanded,
        })}
        data-cy="investment-set-toggle"
        onClick={() => setExpanded(!expanded)}
      />
      <button
        type="button"
        className="main-tree__item-header-switcher"
        data-cy="investment-set-header"
        onClick={() => {
          navigate(INVESTMENT_PATHS[0]);
          dispatch(
            updateFilter({
              type: "investment",
              data: {
                type: "investment",
                data: {
                  type: "SET",
                  data: {
                    setId,
                  },
                },
              },
            })
          );
        }}
      >
        <p className="main-tree__item-header-switcher-text">
          {`${setName} `}
          {numberInvestments && `(${numberInvestments})`}
        </p>
      </button>
    </div>
  );
}

function SetListItem({
  set,
}: {
  set: types.CuratedSetsQuery["setList"]["items"][0];
}) {
  const [expanded, setExpanded] = useState(false);
  const [totalItems, setTotalItems] = useState<number | undefined>();

  const { data, loading, error } = useQuery(FETCH_SET_INVESTMENTS, {
    variables: {
      page: {
        limit: 24,
        offset: 0,
      },
      setId: set.id,
    },
    skip: !expanded,
  });

  useEffect(() => {
    // When we load it and then collapse I wanted to continue showing the
    // total investments for the list.
    // the skip condiition sets data to undefined when collapsed.
    if (data) {
      setTotalItems(data.investmentList.total);
    }
  }, [data]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  if (!set.name || set.id === undefined || set.id === null)
    return <div>Missing Keys investment set name or Id</div>;

  return (
    <div className="main-tree__item">
      <SetTitle
        setName={set.name}
        numberInvestments={totalItems}
        setId={set.id}
        expanded={expanded}
        setExpanded={setExpanded}
      />
      {error && (
        <div className="main-tree__item">
          Something went wrong loading investments
        </div>
      )}
      {loading && (
        <div className="text-center">
          <CircularProgress />
        </div>
      )}
      {expanded && data && (
        <>
          <div className="main-tree__item-content bg-grey-10">
            {data.investmentList.items.map(investment => (
              <FavoriteInvestment investment={investment} key={investment.id} />
            ))}
          </div>
          <div className="text-right pr-15 ">
            <button
              type="button"
              className="in-blue-500"
              onClick={() => {
                navigate(INVESTMENT_PATHS[0]);
                dispatch(
                  updateFilter({
                    type: "investment",
                    data: {
                      type: "investment",
                      data: {
                        type: "SET",
                        data: {
                          setId: set.id,
                        },
                      },
                    },
                  })
                );
              }}
            >
              Show All
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CuratedSetsLists({
  sets,
}: {
  sets: types.CuratedSetsQuery["setList"]["items"];
}) {
  return (
    <div className="main-tree__item-content">
      {sets.map(set => (
        <SetListItem set={set} key={set.id} />
      ))}
    </div>
  );
}

export function CuratedSets() {
  const params = useParams<{ investmentId?: string }>();

  const [expanded, setExpanded] = useState(true);

  const { data, loading, error, refetch } = useQuery(QUERY_CURATED_SETS);

  if (!data) return null;

  if (data.setList.items.length === 0) return null;

  return (
    <li
      className={classNames("main-tree__item", {
        "main-tree__item--open": expanded,
      })}
      data-cy="investment-set-group"
    >
      <div className="main-tree__item-header">
        <span
          className={classNames("main-tree__item-header-toggle", "icon", {
            "icon-arrow": expanded,
            "icon-arrow-right": !expanded,
          })}
          data-cy="investment-set-group-toggle"
          onClick={() => {
            setExpanded(!expanded);
          }}
        />
        <button
          type="button"
          className="main-tree__item-header-switcher"
          data-cy="investment-set-group-header"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          Curated Lists{" "}
          {data.setList.items.length && `(${data.setList.items.length})`}
        </button>
      </div>

      {expanded && <CuratedSetsLists sets={data?.setList?.items || []} />}
    </li>
  );
}
